import { Box, Collapse, ComponentWithAs, Flex, IconProps, Image, Text } from "@chakra-ui/react";
import { useContext } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  AddUserIcon,
  FileIcon,
  MapIcon,
  RanaAlert,
  RanaButton,
  RanaHeading,
  RightArrowIcon,
} from "@nens/ranalib";

import { RanaLink } from "@/components/rana-link";
import { PROCESS_SEARCH_KEY } from "@/config/query-parameter-consts";
import Onboarding from "@/images/onboarding.png";
import { useProcessByTagQuery } from "@/queries/query-process";
import { ProjectContext } from "@/stores/project-provider";

import { OnboardingStatus, useOnboardingStatus } from "../hooks/use-onboarding-status";
import { ProjectTabKey, getProjectTabQueryParam } from "../project-tabs";

interface CheckListItemProps {
  title: string;
  status: OnboardingStatus;
  icon?: ComponentWithAs<"svg", IconProps>;
  tab?: ProjectTabKey;
  queryParamAddon?: string;
}

export function ProjectWelcome() {
  const intl = useIntl();
  const project = useContext(ProjectContext);
  const { data: defineStudyAreaProcess } = useProcessByTagQuery("define_study_area");
  const { onboardingStatusMap, setDoneOnboarding, showProjectWelcome } = useOnboardingStatus();

  function CheckListItem({ title, status, tab, icon, queryParamAddon }: CheckListItemProps) {
    const isLink = status === "not_started" && tab;
    const queryParams =
      isLink && `${getProjectTabQueryParam(tab)}${queryParamAddon ? `&${queryParamAddon}` : ""}`;
    return (
      <RanaAlert
        {...(status === "done" ? { status: "success" } : { overrideIcon: icon || undefined })}
        {...(status === "loading" ? { status: "inProgress" } : undefined)}
        {...(isLink ? { as: RanaLink, queryParams } : {})}
        my={2}
        h={24}
        iconSize={6}
      >
        <Flex w="full" alignItems="center" justifyContent="space-between">
          <Text>{title}</Text> {isLink && <RightArrowIcon />}
        </Flex>
      </RanaAlert>
    );
  }

  return (
    <Collapse in={showProjectWelcome}>
      <Flex
        mb={4}
        pt={8}
        px={8}
        rounded="base"
        borderWidth="base"
        borderColor="brand.grey.200"
        bgGradient="brand.gradient.light"
        flexDirection={{ base: "column", md: "row" }}
      >
        <Box pr={8} w="full">
          <RanaHeading type="h3">
            <FormattedMessage
              defaultMessage="Welcome to the quick start"
              id="projectwelcome.welcome"
            />
          </RanaHeading>
          <RanaHeading type="h6" my={4}>
            <FormattedMessage
              defaultMessage="Congratulations on creating"
              id="projectwelcome.congratulations"
            />{" "}
            <Text as="strong">{project.name}!</Text>
          </RanaHeading>
          <RanaHeading type="h6">
            <FormattedMessage
              defaultMessage="To give your project a strong start, we encourage you to take these essential steps to maximize your experience."
              id="projectwelcome.kickstart"
            />
          </RanaHeading>
          <Image pt={6} maxH="xs" src={Onboarding} />
        </Box>
        <Box p={8} pt={0} w="full" rounded="base">
          <Box m={0}>
            <CheckListItem
              title={intl.formatMessage({
                id: "projectwelcome.create_project",
                defaultMessage: "Create project",
              })}
              status="done"
            />
            <CheckListItem
              title={intl.formatMessage({
                id: "projectwelcome.bring_explore_data",
                defaultMessage: "Add data and explore the possibilities",
              })}
              icon={FileIcon}
              tab="files"
              status={onboardingStatusMap.files}
            />
            <CheckListItem
              title={intl.formatMessage({
                id: "projectwelcome.invite_users",
                defaultMessage: "Invite users to collaborate",
              })}
              icon={AddUserIcon}
              tab="users"
              status={onboardingStatusMap.collaborators}
            />

            <CheckListItem
              title={intl.formatMessage({
                id: "projectwelcome.define_study_area",
                defaultMessage: "Define study area",
              })}
              icon={MapIcon}
              tab="processes"
              queryParamAddon={
                defineStudyAreaProcess
                  ? `${PROCESS_SEARCH_KEY}=${defineStudyAreaProcess.id}`
                  : undefined
              }
              status={onboardingStatusMap.studyArea}
            />
          </Box>
          <Flex justifyContent="flex-end">
            <RanaButton
              onClick={setDoneOnboarding}
              mt={4}
              variant="fourthiary"
              rightIcon={<RightArrowIcon />}
            >
              <FormattedMessage
                defaultMessage="I am prepared to skip the quick start"
                id="projectwelcome.skip_quick_start"
              />
            </RanaButton>
          </Flex>
        </Box>
      </Flex>
    </Collapse>
  );
}
